// Relatório da consulta em PDF (pedido 2026-10-12: "no botão de baixar PDF
// tem que vir um PDF personalizado com todos os dados, tudo certinho").
// Antes o botão abria o PDF do fornecedor (só a parte dele, com a marca
// dele). Agora é um documento GPS Drive com TUDO que a tela mostra:
// identificação, antecedentes/restrições, processos, endereços, telefones,
// e-mails, veículos, vínculos, dados públicos e o detalhe das 5 bases
// jurídicas. Mesma técnica do contrato (client-contract-print-report.tsx):
// sem lib de PDF, DOM A4 em portal no document.body, só visível no
// window.print() → "Salvar como PDF".
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import logoAsset from "@/assets/gps-drive-logo-v3.png.asset.json";
import type { Profile, ProfileList, ProfileRecord } from "./profile";
import {
  benefitSummary,
  brl,
  cleanDetail,
  cnpjMask,
  monthText,
  type PublicData,
} from "./public-data-panel";
import { LEGAL_ORDER, LEGAL_TITLES, type LegalChecks } from "./legal-checks";

export interface ConsultationReportMeta {
  id?: string;
  typeLabel: string;
  createdAt?: string;
  userName?: string | null;
  purpose?: string | null;
  cpf: string;
  demo?: boolean;
}

const fmt = (iso?: string) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

const STATUS_TEXT: Record<ProfileRecord["status"], string> = {
  nada_consta: "Nada consta",
  consta: "Consta",
  info: "Ver dados",
};

// Dados de uma base em "campo: valor" (o formato real varia por base).
function flatten(v: unknown, prefix = "", out: [string, string][] = [], depth = 0): [string, string][] {
  if (out.length >= 300 || depth > 8) return out;
  if (v == null || v === "") return out;
  if (typeof v !== "object") {
    const s = String(v).trim();
    // base64/blobs não cabem num relatório
    if (s && s.length < 600 && !/^data:/.test(s)) out.push([prefix || "valor", s]);
    return out;
  }
  if (Array.isArray(v)) {
    v.forEach((item, i) => flatten(item, `${prefix}${prefix ? " " : ""}#${i + 1}`, out, depth + 1));
    return out;
  }
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    const label = k.replace(/_/g, " ").toLowerCase();
    flatten(val, prefix ? `${prefix} › ${label}` : label, out, depth + 1);
  }
  return out;
}

function Section({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <section className="cr-section">
      <h2>
        {title}
        {count != null && <span className="cr-count">{count}</span>}
      </h2>
      {children}
    </section>
  );
}

const Empty = ({ children = "Nada consta." }: { children?: ReactNode }) => (
  <p className="cr-empty">{children}</p>
);

function Table({ head, rows }: { head: string[]; rows: (string | undefined)[][] }) {
  return (
    <table className="cr-table">
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j}>{c || "—"}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ListSection({
  title,
  profile,
  list,
  head,
  row,
}: {
  title: string;
  profile: Profile;
  list: ProfileList;
  head: string[];
  row: (i: number) => (string | undefined)[];
}) {
  const items = profile[list];
  if (profile.notCovered?.[list])
    return (
      <Section title={title}>
        <Empty>Não incluído neste tipo de consulta.</Empty>
      </Section>
    );
  return (
    <Section title={title} count={items.length}>
      {items.length ? <Table head={head} rows={items.map((_, i) => row(i))} /> : <Empty>Nenhum registro encontrado.</Empty>}
    </Section>
  );
}

function PublicSection<T>({
  title,
  section,
  head,
  row,
}: {
  title: string;
  section: { status: string; message?: string; items: T[] };
  head: string[];
  row: (it: T) => (string | undefined)[];
}) {
  return (
    <div className="cr-sub">
      <h3>
        {title} <span className="cr-count">{section.items.length}</span>
      </h3>
      {section.status === "not_configured" ? (
        <Empty>Fonte pública ainda em ativação.</Empty>
      ) : section.status === "error" && !section.items.length ? (
        <Empty>Fonte indisponível no momento.</Empty>
      ) : section.items.length ? (
        <Table head={head} rows={section.items.map(row)} />
      ) : (
        <Empty />
      )}
    </div>
  );
}

export function ConsultationReportDocument({
  meta,
  profile,
  publicData,
  legal,
}: {
  meta: ConsultationReportMeta;
  profile: Profile;
  publicData: PublicData | null;
  legal: LegalChecks | null;
}) {
  const p = profile;
  const ident: [string, string | undefined][] = [
    ["Nome", p.name],
    ["CPF", meta.cpf],
    ["RG", p.rg],
    ["Nascimento", p.birthDate],
    ["Sexo", p.gender],
    ["Situação do CPF", p.status],
    ["Nome da mãe", p.mother],
    ["Nome do pai", p.father],
    ["Nacionalidade", p.nationality],
    ["Naturalidade", p.birthplace],
    ...p.extras.map((e) => [e.label, e.value] as [string, string]),
  ];
  const consta = p.records.filter((r) => r.status === "consta").length + p.processes.length;

  return (
    <div className="cr-doc">
      <header className="cr-header">
        <img src={logoAsset.url} alt="GPS Drive" />
        <div className="cr-header-text">
          <div className="cr-title">Relatório de Consulta</div>
          <div className="cr-subtitle">{meta.typeLabel}</div>
        </div>
        <div className="cr-header-meta">
          <div>
            <b>Emitido em</b> {fmt(new Date().toISOString())}
          </div>
          <div>
            <b>Consulta</b> {fmt(meta.createdAt)}
          </div>
          {meta.id && (
            <div>
              <b>Protocolo</b> {meta.id.slice(0, 8).toUpperCase()}
            </div>
          )}
        </div>
      </header>

      {meta.demo && <div className="cr-demo">DEMONSTRAÇÃO — DADOS FICTÍCIOS</div>}

      <div className="cr-hero">
        {p.photo && <img src={p.photo} alt="" className="cr-photo" />}
        <div className="cr-hero-main">
          <div className="cr-name">{p.name ?? "Nome não informado"}</div>
          <div className="cr-cpf">CPF {meta.cpf}</div>
          <div className="cr-hero-info">
            {meta.userName && <span>Consultado por: {meta.userName}</span>}
            {meta.purpose && <span>Finalidade: {meta.purpose}</span>}
          </div>
        </div>
        <div className={consta ? "cr-verdict cr-verdict-bad" : "cr-verdict cr-verdict-ok"}>
          {consta ? `${consta} apontamento${consta > 1 ? "s" : ""}` : "Nenhum apontamento"}
        </div>
      </div>

      <Section title="Identificação">
        <div className="cr-grid">
          {ident
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k} className="cr-field">
                <span>{k}</span>
                <b>{v}</b>
              </div>
            ))}
        </div>
      </Section>

      <Section title="Antecedentes e restrições" count={p.records.length}>
        {p.notCovered?.records ? (
          <Empty>Não incluído neste tipo de consulta.</Empty>
        ) : p.records.length ? (
          <table className="cr-table">
            <thead>
              <tr>
                <th>Base</th>
                <th>Resultado</th>
                <th>Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {p.records.map((r, i) => (
                <tr key={i}>
                  <td>{r.title}</td>
                  <td>
                    <span className={`cr-tag cr-tag-${r.status}`}>{STATUS_TEXT[r.status]}</span>
                  </td>
                  <td>{r.detail ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>Nenhum registro encontrado.</Empty>
        )}
      </Section>

      <ListSection
        title="Processos judiciais"
        profile={p}
        list="processes"
        head={["Número", "Tribunal / vara", "Assunto", "Situação", "Data"]}
        row={(i) => {
          const x = p.processes[i];
          return [x.number, x.court, x.subject, x.status, x.date];
        }}
      />
      <ListSection
        title="Endereços"
        profile={p}
        list="addresses"
        head={["Endereço", "Bairro", "Cidade", "CEP", "Tipo / data"]}
        row={(i) => {
          const x = p.addresses[i];
          return [x.line, x.district, x.city, x.zip, [x.kind, x.date].filter(Boolean).join(" · ")];
        }}
      />
      <ListSection
        title="Telefones"
        profile={p}
        list="phones"
        head={["Número", "Tipo", "Operadora"]}
        row={(i) => {
          const x = p.phones[i];
          return [x.number, x.kind, x.carrier];
        }}
      />
      <ListSection
        title="E-mails"
        profile={p}
        list="emails"
        head={["E-mail"]}
        row={(i) => [p.emails[i].address]}
      />
      <ListSection
        title="Veículos"
        profile={p}
        list="vehicles"
        head={["Placa", "Modelo", "Ano", "Cor"]}
        row={(i) => {
          const x = p.vehicles[i];
          return [x.plate, x.model, x.year, x.color];
        }}
      />
      <ListSection
        title="Vínculos e empresas"
        profile={p}
        list="links"
        head={["Nome", "Relação", "Documento"]}
        row={(i) => {
          const x = p.links[i];
          return [x.name, x.relation, x.document];
        }}
      />

      {publicData && (
        <Section title="Dados públicos">
          <p className="cr-note">
            Portal da Transparência (CGU) e Receita Federal — consultado em {fmt(publicData.fetchedAt)}.
          </p>
          <PublicSection
            title="Sanções (CEIS / CNEP / CEAF)"
            section={publicData.sanctions}
            head={["Cadastro", "Tipo", "Órgão", "Período"]}
            row={(s) => [s.list, s.type, s.organ, [s.start, s.end].filter(Boolean).join(" → ")]}
          />
          <PublicSection
            title="Pessoa exposta politicamente (PEP)"
            section={publicData.peps}
            head={["Função", "Órgão", "Período"]}
            row={(s) => [s.role, s.organ, [s.start, s.end].filter(Boolean).join(" → ")]}
          />
          <PublicSection
            title="Servidor público"
            section={publicData.servants}
            head={["Situação", "Vínculo", "Órgão", "Cargo", "Desde", "UF"]}
            row={(s) => [s.situation, s.kind, s.organ, s.role, s.since, s.uf]}
          />
          <div className="cr-sub">
            <h3>
              Benefícios sociais <span className="cr-count">{publicData.benefits.items.length}</span>
            </h3>
            {publicData.benefits.status === "not_configured" ? (
              <Empty>Fonte pública ainda em ativação.</Empty>
            ) : publicData.benefits.items.length ? (
              <>
                <Table
                  head={["Programa", "Parcelas", "Total", "Período"]}
                  rows={benefitSummary(publicData.benefits.items).map(([prog, g]) => [
                    prog,
                    String(g.count),
                    brl(g.total),
                    [monthText(g.first), monthText(g.last)].filter(Boolean).join(" → "),
                  ])}
                />
                <Table
                  head={["Programa", "Mês", "Valor", "Cidade", "Detalhe"]}
                  rows={publicData.benefits.items.map((b) => [
                    b.program,
                    monthText(b.month),
                    brl(b.value),
                    b.city,
                    cleanDetail(b.detail),
                  ])}
                />
              </>
            ) : publicData.benefits.status === "error" ? (
              <Empty>Fonte indisponível no momento.</Empty>
            ) : (
              <Empty />
            )}
          </div>
          <PublicSection
            title="Viagens a serviço"
            section={publicData.travels}
            head={["Ano", "Motivo", "Órgão", "Período", "Valor"]}
            row={(s) => [
              s.year != null ? String(s.year) : undefined,
              s.reason,
              s.organ,
              [s.start, s.end].filter(Boolean).join(" → "),
              brl(s.value),
            ]}
          />
          <PublicSection
            title="Empresas (Receita Federal)"
            section={publicData.companies}
            head={["CNPJ", "Razão social", "Situação", "Abertura", "Atividade", "Cidade", "Capital", "Sócios"]}
            row={(c) => [
              cnpjMask(c.cnpj),
              [c.name, c.tradeName].filter(Boolean).join(" / "),
              c.status,
              c.openedAt,
              c.activity,
              c.city,
              brl(c.capital),
              c.partners.map((s) => [s.name, s.role].filter(Boolean).join(" – ")).join("; "),
            ]}
          />
        </Section>
      )}

      {legal && (
        <Section title="Detalhe das bases jurídicas">
          <p className="cr-note">Consultado em {fmt(legal.fetchedAt)}.</p>
          {LEGAL_ORDER.filter((k) => legal.checks[k]).map((k) => {
            const c = legal.checks[k]!;
            const rows = c.status === "ok" ? flatten(c.data) : [];
            return (
              <div key={k} className="cr-sub">
                <h3>{LEGAL_TITLES[k]}</h3>
                {c.status !== "ok" ? (
                  <Empty>Base indisponível no momento{c.message ? ` (${c.message})` : ""}.</Empty>
                ) : rows.length ? (
                  <table className="cr-table cr-kv">
                    <tbody>
                      {rows.map(([key, val], i) => (
                        <tr key={i}>
                          <th>{key}</th>
                          <td>{val}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <Empty>Nada consta nesta base.</Empty>
                )}
              </div>
            );
          })}
        </Section>
      )}

      <footer className="cr-footer">
        Documento gerado pela plataforma GPS Drive. Informações obtidas de bases de dados públicas e
        privadas na data da consulta, de uso restrito à finalidade informada e protegidas pela LGPD
        (Lei 13.709/2018). É proibida a divulgação a terceiros.
      </footer>
    </div>
  );
}

// Monta o documento só na hora de imprimir e chama o window.print(); o
// título da aba vira o nome do arquivo sugerido no "Salvar como PDF".
export function ConsultationPrintReport({
  open,
  onDone,
  fileName,
  ...docProps
}: Parameters<typeof ConsultationReportDocument>[0] & {
  open: boolean;
  onDone: () => void;
  fileName: string;
}) {
  useEffect(() => {
    if (!open) return;
    const prevTitle = document.title;
    document.title = fileName;
    const finish = () => {
      document.title = prevTitle;
      onDone();
    };
    window.addEventListener("afterprint", finish, { once: true });
    // espera o portal pintar (e a logo/foto carregarem) antes de imprimir
    const t = window.setTimeout(() => window.print(), 400);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("afterprint", finish);
      document.title = prevTitle;
    };
  }, [open, fileName, onDone]);

  if (!open) return null;
  return createPortal(
    <div className="cr-print-only">
      <ConsultationReportDocument {...docProps} />
      <style>{REPORT_CSS}</style>
    </div>,
    document.body,
  );
}

const REPORT_CSS = `
@media screen { .cr-print-only { display: none; } }
@media print {
  body * { visibility: hidden; }
  .cr-print-only, .cr-print-only * { visibility: visible; }
  .cr-print-only { display: block; position: absolute; inset: 0; background: #fff; }
  @page { size: A4; margin: 12mm; }
}
.cr-doc { font-family: Inter, Arial, sans-serif; color: #0f172a; font-size: 10.5px; line-height: 1.4;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.cr-header { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; border-bottom: 3px solid #059669; }
.cr-header img { height: 44px; width: auto; }
.cr-header-text { flex: 1; }
.cr-title { font-size: 18px; font-weight: 800; letter-spacing: .02em; }
.cr-subtitle { font-size: 11px; color: #059669; font-weight: 700; text-transform: uppercase; letter-spacing: .12em; }
.cr-header-meta { text-align: right; font-size: 9.5px; color: #475569; }
.cr-header-meta b { color: #0f172a; margin-right: 4px; }
.cr-demo { margin-top: 8px; padding: 6px; text-align: center; font-weight: 800; color: #92400e; background: #fef3c7; border: 1px solid #f59e0b; border-radius: 6px; }
.cr-hero { display: flex; align-items: center; gap: 14px; margin-top: 12px; padding: 12px 14px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; }
.cr-photo { width: 64px; height: 64px; object-fit: cover; border-radius: 8px; }
.cr-hero-main { flex: 1; min-width: 0; }
.cr-name { font-size: 16px; font-weight: 800; }
.cr-cpf { font-family: ui-monospace, monospace; font-size: 12px; color: #334155; margin-top: 2px; }
.cr-hero-info { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-top: 4px; font-size: 9.5px; color: #475569; }
.cr-verdict { padding: 6px 10px; border-radius: 8px; font-weight: 800; font-size: 11px; white-space: nowrap; }
.cr-verdict-ok { background: #059669; color: #fff; }
.cr-verdict-bad { background: #dc2626; color: #fff; }
.cr-section { margin-top: 14px; }
.cr-section h2 { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .1em;
  color: #065f46; border-bottom: 1px solid #d1fae5; padding-bottom: 3px; margin-bottom: 6px; break-after: avoid; }
.cr-sub { margin-top: 8px; }
.cr-sub h3 { font-size: 10.5px; font-weight: 700; color: #1e293b; margin-bottom: 4px; break-after: avoid; }
.cr-count { display: inline-block; min-width: 18px; padding: 0 5px; border-radius: 9px; background: #e2e8f0; color: #334155; font-size: 9px; font-weight: 700; text-align: center; }
.cr-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 14px; }
.cr-field { display: flex; flex-direction: column; break-inside: avoid; }
.cr-field span { font-size: 8.5px; text-transform: uppercase; letter-spacing: .08em; color: #64748b; }
.cr-field b { font-size: 10.5px; word-break: break-word; }
.cr-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; font-size: 10px; }
.cr-table th { background: #f1f5f9; text-align: left; font-size: 8.5px; text-transform: uppercase; letter-spacing: .06em; color: #475569; padding: 4px 6px; border: 1px solid #e2e8f0; }
.cr-table td { padding: 4px 6px; border: 1px solid #e2e8f0; vertical-align: top; word-break: break-word; }
.cr-table tr { break-inside: avoid; }
.cr-kv th { width: 34%; text-transform: none; letter-spacing: 0; font-size: 9px; font-weight: 600; }
.cr-tag { display: inline-block; padding: 1px 6px; border-radius: 4px; font-size: 9px; font-weight: 800; text-transform: uppercase; white-space: nowrap; }
.cr-tag-nada_consta { background: #dcfce7; color: #166534; }
.cr-tag-consta { background: #fee2e2; color: #991b1b; }
.cr-tag-info { background: #e0f2fe; color: #075985; }
.cr-empty { color: #64748b; font-style: italic; padding: 4px 0; }
.cr-note { color: #64748b; font-size: 9px; margin-bottom: 2px; }
.cr-footer { margin-top: 18px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 8.5px; color: #64748b; text-align: center; }
`;
